/**
 * Typed mirrors of the Pydantic response shapes (CdC §7.3 / §7.5).
 * Kept terse — only the fields the UI actually consumes.
 */

export type PaymentMode = 'baj' | 'paying';

/**
 * Format ``ref_cabinet`` avec le mode de paiement entre parenthèses
 * (Sprint 2 §9.3). Retourne ``"24/567"`` si le mode n'est pas défini,
 * ``"24/567 (BAJ)"`` ou ``"24/567 (Payant)"`` sinon.
 */
export function formatDossierRef(refCabinet: string, paymentMode?: PaymentMode | null): string {
  if (!paymentMode) return refCabinet;
  return `${refCabinet} (${paymentMode === 'baj' ? 'BAJ' : 'Payant'})`;
}

export type DossierListItem = {
  id: string;
  ref_cabinet: string;
  titre: string | null;
  client_name: string | null;
  status: string;
  payment_mode?: PaymentMode | null;
  last_activity_at: string;
  created_at: string;
  pieces_count: number;
  pages_total: number;
  pages_treated: number;
  annotations_count: number;
};

export type Dossier = DossierListItem & {
  tenant_id: string;
  ref_parquet: string | null;
  ref_forlex: string | null;
  juridiction: string | null;
  numero_role: string | null;
  created_by: string;
  archive_scheduled_at: string | null;
  has_dek: boolean;
  updated_at: string;
  access_mode?: 'all' | 'restricted';
  payment_mode?: PaymentMode | null;
  general_note?: string | null;
  /** Date des faits — utilisée pour dériver le régime corpus dommage
   *  (ancien CC vs Livre 6 nouveau CC, pivot 1/1/2025). */
  date_faits?: string | null;
  /** Phase 2 — métadonnées extraites par l'orchestrateur Analyse IA :
   * chambre, juge_instruction, procureur, greffier, dates clés, confidence. */
  ai_extracted_metadata?: {
    chambre?: string | null;
    juge_instruction?: string | null;
    procureur?: string | null;
    greffier?: string | null;
    date_audience?: string | null;
    date_ordre_citer?: string | null;
    date_requisitoire?: string | null;
    confidence?: number;
    [k: string]: unknown;
  } | null;
};

export type Collaborator = {
  user_id: string;
  display_name: string;
  email: string;
  permission: string;
  added_at: string;
};

export type PdfReviewStatus = 'non_vue' | 'en_cours' | 'analysee' | 'a_relire';

export type PdfMetadata = {
  id: string;
  dossier_id: string;
  filename_original: string;
  file_size_bytes: number;
  file_hash_sha256: string;
  page_count: number | null;
  is_scanned: boolean | null;
  ocr_status: string;
  ai_extraction_status: string;
  numero_pv: string | null;
  titre_pv: string | null;
  date_pv: string | null;
  versement_id: string | null;
  inventaire_entry_id: string | null;
  uploaded_by: string;
  uploaded_at: string;
  // J1 — statut d'analyse manuel (override la dérivation automatique).
  review_status?: PdfReviewStatus;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
};

export type AnnotationCategory = 'favorable' | 'defavorable' | 'important' | 'a_verifier';

export type Annotation = {
  id: string;
  dossier_id: string;
  pdf_file_id: string | null;
  piece_client_id: string | null;
  page_number: number;
  category: AnnotationCategory;
  text_extract: string;
  coords_json: { x: number; y: number; w: number; h: number }[];
  comment: string | null;
  ref_numero_pv: string | null;
  ref_titre_pv: string | null;
  ref_page_pv: number | null;
  ref_carton: string | null;
  ref_sous_farde: string | null;
  ref_piece: string | null;
  current_version: number;
  created_by: string;
  creator_display_name: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
  linked_party_ids: string[];
  linked_tag_ids: string[];
};

export type Tag = {
  id: string;
  dossier_id: string;
  label: string;
  color: string | null;
  created_by: string;
  created_at: string;
};

export type InventaireEntry = {
  id: string;
  dossier_id: string;
  carton: string | null;
  sous_farde: string | null;
  piece: string | null;
  titre_piece: string | null;
  type_piece: string | null;
  date_piece: string | null;
  nombre_pages: number | null;
  source: string;
  confidence: number | null;
  created_at: string;
};

export type PieceClientStatutDepot =
  | 'non_deposee'
  | 'a_deposer'
  | 'deposee'
  | 'communiquee'
  | 'retiree'
  | 'rejetee';

export type AuditLogEntry = {
  id: number;
  user_id: string | null;
  actor_display_name: string | null;
  dossier_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
  previous_hash: string;
  entry_hash: string;
};

export type PieceClient = {
  id: string;
  dossier_id: string;
  titre: string;
  description: string | null;
  date_piece: string | null;
  statut_depot: PieceClientStatutDepot;
  date_depot: string | null;
  reference_depot: string | null;
  page_count: number | null;
  ocr_status: string;
  conversion_status: 'pending' | 'processed' | 'failed' | 'unsupported' | 'skipped';
  conversion_error: string | null;
  has_file: boolean;
  uploaded_by: string;
  uploaded_at: string;
};

export type ExportJobStatus = {
  job_id: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  download_url?: string | null;
  error?: string | null;
};

export type PartieRole =
  | 'prevenu'
  | 'partie_civile'
  | 'temoin'
  | 'enqueteur'
  | 'expert'
  | 'victime'
  | 'autre';

export type Partie = {
  id: string;
  dossier_id: string;
  nom: string;
  prenom: string | null;
  role: PartieRole;
  qualite: string | null;
  is_client: boolean;
  notes: string | null;
  created_at: string;
};

export type TimelineEvent = {
  id: string;
  dossier_id: string;
  pdf_file_id: string | null;
  event_date: string; // YYYY-MM-DD
  event_time: string | null; // HH:MM:SS
  event_type: string | null;
  description: string;
  source_quote: string | null;
  confidence: number | null;
  verified: boolean;
  verified_by: string | null;
  is_future?: boolean;
  is_checked?: boolean;
  created_at: string;
  linked_party_ids: string[];
};

export type DossierNote = {
  id: string;
  dossier_id: string;
  pdf_file_id: string | null;
  piece_client_id: string | null;
  page_number: number | null;
  content: string;
  sort_key: number | null;
  created_by: string;
  creator_display_name: string | null;
  created_at: string;
  updated_at: string;
};

/** Citation Anthropic dans un bloc de réponse assistant. */
export type ChatCitation = {
  type: string | null;
  cited_text: string | null;
  document_index: number | null;
  document_title: string | null;
  context: { pdf_id?: string; page?: number; filename?: string } | null;
  start_char_index: number | null;
  end_char_index: number | null;
};

export type ChatBlock = {
  type: 'text';
  text: string;
  citations: ChatCitation[];
};

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations: ChatBlock[] | null;
  created_by: string | null;
  created_at: string;
};

export type ChatResponse = {
  chat_id: string;
  dossier_id: string;
  title: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  messages: ChatMessage[];
};

export type ChatSummary = {
  id: string;
  title: string | null;
  is_archived: boolean;
  message_count: number;
  created_at: string;
  updated_at: string;
};

export type ChatCostEstimate = {
  mode: 'stuff' | 'retrieval';
  total_pages_ocr: number;
  indexed_pages: number;
  total_chars: number;
  estimated_input_tokens_per_question: number;
  estimated_output_tokens_per_question: number;
  cost_per_question_first_usd: number;
  cost_per_question_cached_usd: number;
  cost_per_question_first_eur: number;
  cost_per_question_cached_eur: number;
  indexation_cost_one_shot_usd: number | null;
  indexation_cost_one_shot_eur: number | null;
  voyage_available: boolean;
};

export type AiCostBreakdownEntry = {
  count: number;
  usd: number;
  eur: number;
};

export type AiCostTotal = {
  total_usd: number;
  total_eur: number;
  currency: string;
  breakdown: {
    chat_messages: AiCostBreakdownEntry;
    auto_title: AiCostBreakdownEntry;
    inventaire_import: AiCostBreakdownEntry;
    embeddings_indexation: AiCostBreakdownEntry;
    ai_research?: AiCostBreakdownEntry;
    conclusions_gen?: AiCostBreakdownEntry;
    actes_gen?: AiCostBreakdownEntry;
    briefings_gen?: AiCostBreakdownEntry;
    memos_gen?: AiCostBreakdownEntry;
    detections?: AiCostBreakdownEntry;
    mistral_ocr?: AiCostBreakdownEntry;
  };
};

// ---------------------------------------------------------------------------
// État de la procédure (ADR-026)
// ---------------------------------------------------------------------------

export type ProcedurePhase =
  | 'preliminaire'
  | 'instruction'
  | 'reglement'
  | 'fond_correctionnel'
  | 'fond_assises'
  | 'appel_correctionnel'
  | 'cassation'
  | 'execution'
  | 'clos';

export type DossierRole = 'suspect_prevenu' | 'partie_civile';

export type DetentionStatus = 'libre' | 'detenu' | 'libere_conditions' | 'bracelet' | 'internement';

export type ProcedureEventType =
  // Préliminaire
  | 'plainte'
  | 'constitution_pc_parquet'
  | 'information_ouverte'
  | 'requisitoire_mise_instruction'
  // Instruction
  | 'inculpation'
  | 'mandat_arret'
  | 'mandat_amener'
  | 'audition_inculpe'
  | 'cc_passage_initial'
  | 'cc_controle_detention'
  | 'cma_appel_detention'
  | 'libere_sous_conditions'
  | 'libere'
  | 'requete_61ter'
  | 'ordonnance_61ter'
  | 'requete_61quater'
  | 'ordonnance_61quater'
  | 'requete_61quinquies'
  | 'ordonnance_61quinquies'
  | 'avis_fin_instruction'
  | 'conclusions_127'
  | 'requisitoire_final'
  // Règlement
  | 'cc_audience_reglement'
  | 'cc_ordonnance_renvoi_corr'
  | 'cc_ordonnance_renvoi_assises'
  | 'cc_ordonnance_non_lieu'
  | 'cc_ordonnance_dessaisissement'
  | 'cma_appel_ordonnance'
  | 'cma_arret_reglement'
  // Fond correctionnel
  | 'citation_corr'
  | 'audience_intro_corr'
  | 'depot_conclusions_corr'
  | 'audience_debats_corr'
  | 'jugement_corr_contradictoire'
  | 'jugement_corr_defaut'
  | 'signification_jugement'
  | 'opposition'
  // Fond assises
  | 'mise_accusation'
  | 'audience_ouverture_assises'
  | 'verdict_assises'
  | 'arret_assises'
  // Appel
  | 'acte_appel'
  | 'citation_ca'
  | 'audience_intro_ca'
  | 'audience_debats_ca'
  | 'arret_ca'
  // Cassation
  | 'pourvoi_cassation'
  | 'memoire_cassation'
  | 'arret_cassation'
  // Exécution
  | 'signification_definitive'
  | 'ecrou'
  | 'liberation_definitive'
  | 'liberation_conditionnelle'
  | 'autre';

export type DeadlineKind =
  | 'cc_initial_5j'
  | 'cma_detention_24h'
  | 'appel_cma_reglement_15j'
  | 'opposition_15j'
  | 'appel_corr_30j'
  | 'cassation_15j'
  | 'appel_assises_30j';

export type DeadlineAlertStatus = 'ok' | 'soon' | 'urgent' | 'overdue';

export type ProcedureState = {
  id: string;
  dossier_id: string;
  current_phase: ProcedurePhase;
  dossier_role: DossierRole;
  detention_status: DetentionStatus | null;
  detention_start_date: string | null;
  juridiction: string | null;
  next_hearing_date: string | null;
  next_hearing_type: string | null;
  client_facing_mode: boolean;
  ai_proposed_phase: ProcedurePhase | null;
  ai_proposed_at: string | null;
  ai_proposed_rationale: string | null;
  // Sprint 2 Timeline (ADR-041) — sous-état mutuellement exclusif au sein
  // de la phase courante (ex. instruction.devoirs_en_cours) ; flag variante
  // assises (active fond_assises au lieu de fond_correctionnel).
  sub_state: string | null;
  variante_assises: boolean;
  created_at: string;
  updated_at: string;
};

export type ProcedureEvent = {
  id: string;
  dossier_id: string;
  event_type: ProcedureEventType;
  event_date: string;
  label: string | null;
  label_client: string | null;
  details: string | null;
  source_pdf_id: string | null;
  source_page: number | null;
  is_manual: boolean;
  is_ai_proposed: boolean;
  is_validated: boolean;
  is_future: boolean;
  is_checked: boolean;
  triggers_deadline_kind: DeadlineKind | null;
  deadline_date: string | null;
  deadline_alert_status: DeadlineAlertStatus | null;
  created_at: string;
  updated_at: string;
};

export type ProcedureBundle = {
  state: ProcedureState;
  events: ProcedureEvent[];
};

// ---------------------------------------------------------------------------
// Conclusions IA + RAG corpus (ADR-027)
// ---------------------------------------------------------------------------

export type ConclusionType =
  | 'cc_reglement'
  | 'cmi_recours'
  | 'fond_correctionnel'
  | 'appel_correctionnel'
  | 'fond_assises'
  | 'cassation'
  | 'autre';

export type ConclusionStatus = 'draft' | 'finalized' | 'archived';

export type ConclusionSection = {
  heading: string;
  body_markdown: string;
};

export type ConclusionCitation = {
  source_id: string;
  source_type: 'doctrine' | 'jurisprudence_cass' | 'code_legal';
  title: string;
  author: string | null;
  year: number | null;
  locator: string;
  score: number;
};

export type Conclusion = {
  id: string;
  dossier_id: string;
  conclusion_type: ConclusionType;
  title: string;
  sections: ConclusionSection[];
  corpus_citations: ConclusionCitation[];
  model_used: string | null;
  prompt_version: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_eur: number | null;
  status: ConclusionStatus;
  created_at: string;
  updated_at: string;
};

// ---------------------------------------------------------------------------
// Actes générés IA (ADR-031)
// ---------------------------------------------------------------------------

export type ActeType =
  | 'requete_61ter'
  | 'requete_61quater'
  | 'requete_61quinquies'
  | 'constitution_pc_franchimont'
  | 'opposition'
  | 'acte_appel'
  | 'pourvoi_cassation'
  | 'autre';

export type ActeStatus = 'draft' | 'finalized' | 'archived';

export type Acte = {
  id: string;
  dossier_id: string;
  acte_type: ActeType;
  title: string;
  body_markdown: string;
  fields_filled: Record<string, string>;
  source_template_id: string | null;
  source_template_title: string | null;
  source_template_download_url: string | null;
  model_used: string | null;
  prompt_version: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_eur: number | null;
  status: ActeStatus;
  created_at: string;
  updated_at: string;
};

/** Marqueur de page (ou de pièce entière si `page_number` null). */
export type Bookmark = {
  id: string;
  dossier_id: string;
  pdf_file_id: string | null;
  piece_client_id: string | null;
  page_number: number | null;
  created_by: string;
  creator_display_name: string | null;
  created_at: string;
};
